import {
  Button,
  Container,
  Text,
  Card,
  SimpleGrid,
  ThemeIcon,
  rem,
} from "@mantine/core";
import { motion } from "framer-motion";
import { IconUserPlus, IconClick, IconPlayerPlay } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { Colors } from "../../../Lib/Data";

import "./styles.scss";

// Variance for animations
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
    },
  },
};

export default function HomeHowItWorks() {
  const steps = [
    {
      icon: IconUserPlus,
      title: "Create Account",
      description:
        "Sign up in seconds. We keep your data secure while you manage your shared subscriptions in one place.",
      link: "/sign-up",
      linkLabel: "Get Started",
      color: "blue",
    },
    {
      icon: IconClick,
      title: "Choose Plan",
      description:
        "Browse our catalog of premium services like Netflix, Spotify, and YouTube. Pick the plan and duration that suits needed.",
      link: "/d/services",
      linkLabel: "View Plans",
      color: "grape",
    },
    {
      icon: IconPlayerPlay,
      title: "Start Streaming",
      description:
        "Instant access! Credentials are provided immediately after payment so you can start enjoying your content right away.",
      color: "green",
    },
  ];

  return (
    <Container size="lg" id="works" py={80}>
      <div className="home-how-it-works-container">
        <div style={{ textAlign: "center", marginBottom: 50 }}>
          <Text
            fw={700}
            c={Colors.GreenSecondary}
            style={{ fontSize: rem(36), lineHeight: 1.2 }}
          >
            How it Works
          </Text>
          <Text c="dimmed" mt="md" size="lg" maw={600} mx="auto">
            Get premium access for a fraction of the price in three simple
            steps. Shared subscriptions have never been easier!
          </Text>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing={30}>
            {steps.map((step, index) => (
              <motion.div
                variants={itemVariants}
                key={index}
                style={{ height: "100%" }}
              >
                <Card
                  padding="xl"
                  radius="lg"
                  withBorder
                  style={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    cursor: "default",
                  }}
                  className="hover-card"
                >
                  <ThemeIcon
                    size={60}
                    radius="md"
                    variant="light"
                    color={step.color}
                    mb="xl"
                  >
                    <step.icon style={{ width: rem(30), height: rem(30) }} />
                  </ThemeIcon>

                  <Text fz="xl" fw={700} mb="sm" className="title">
                    {step.title}
                  </Text>

                  <Text c="dimmed" mb="xl" style={{ flex: 1, lineHeight: 1.6 }}>
                    {step.description}
                  </Text>

                  {step.link && (
                    <Link to={step.link} style={{ width: "100%" }}>
                      <Button
                        fullWidth
                        variant="light"
                        color={step.color}
                        radius="md"
                      >
                        {step.linkLabel}
                      </Button>
                    </Link>
                  )}
                </Card>
              </motion.div>
            ))}
          </SimpleGrid>
        </motion.div>
      </div>

      <style>{`
        .hover-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.05);
          border-color: transparent;
        }
      `}</style>
    </Container>
  );
}
